import { useMemo, useState } from "react";
import Table from "../../components/Table";
import {
  AccountHolder,
  AccountHolderUser,
} from "../../services/accountService";
import { getEmptyUser } from "../../utils/users";
import { getUserDetailsTableColumns } from "./userDetailsTableColumns";
import Error from "../../components/Error";
import Button from "../../components/Button";
import HasAccess, { csrAdminRoles } from "../../components/HasAccess";
import Modal from "../../components/Modal";
import SignUpForm from "../onboarding/SignUpForm";
import { useSnackbar } from "../../components/Snackbar";
import useCreateUserMutation from "../../hooks/data/mutations/useCreateUserMutation";
import { useQueryClient } from "@tanstack/react-query";
import { SignUpFormData } from "../../services/userService";
import { PlusIcon } from "@heroicons/react/24/solid";
import Panel from "./Panel";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import useUpdateUserStatusMutation from "../../hooks/data/mutations/useUpdateUserStatusMutation";
import Spinner from "../../components/Spinner";
import useUserDetailsQuery from "../../hooks/data/queries/useUserDetailsQuery";

type Props = {
  accountHolderCode: string;
  accountConfigData?: AccountHolder;
};

function MerchantUsers({
  accountHolderCode,
  accountConfigData,
}: Props): JSX.Element {
  const { data: currentUserAccountData } = useCurrentUserAccount();
  const {
    data: userDetailsData,
    isLoading,
    error,
  } = useUserDetailsQuery(accountHolderCode);

  const memoUsers = useMemo(() => {
    if (isLoading && !userDetailsData) {
      return Array.from<AccountHolderUser>({ length: 5 }).fill(getEmptyUser());
    }
    return userDetailsData?.at(0)?.userInfo ?? [];
  }, [userDetailsData, isLoading]);

  const { mutate, status } = useCreateUserMutation();
  const { mutate: updateUserStatus, isLoading: isUpdatingUserStatus } =
    useUpdateUserStatusMutation();

  const [showSignUpModal, setShowSignupModal] = useState(false);
  const [showUpdateUserStatusModal, setShowUpdateUserStatusModal] =
    useState(false);
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentUserStatus, setCurrentUserStatus] = useState(false);

  const queryClient = useQueryClient();

  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  async function handleCreateUserFormSubmit(signupFormData: SignUpFormData) {
    mutate(signupFormData, {
      onSuccess: ({ firstName, lastName }) => {
        openSnackbar(`User ${firstName} ${lastName} was successfully added.`, {
          type: "success",
        });
        queryClient.invalidateQueries(["userDetails", accountHolderCode]);
      },
      onError: (error) => {
        openSnackbar(error, { type: "error" });
      },
      onSettled: () => {
        setShowSignupModal(false);
      },
    });
  }

  function handleUpdateUserStatus() {
    updateUserStatus(
      {
        id: currentUserId,
        enabled: !currentUserStatus,
      },
      {
        onSuccess() {
          queryClient.invalidateQueries(["userDetails"]);
          openSnackbar("User status updated.", {
            type: "success",
          });
        },
        onError(e) {
          openSnackbar(e, {
            type: "error",
          });
        },
        onSettled() {
          setShowUpdateUserStatusModal(false);
        },
      }
    );
  }

  return (
    <>
      <Panel className="col-span-12">
        <Panel.Header title="Merchant Users">
          <HasAccess allowedRoles={[...csrAdminRoles]}>
            <Button
              custom-id="addMerchantUserButton"
              size="sm"
              onClick={() => setShowSignupModal(true)}
              disabled={isLoading}
              icon={<PlusIcon />}
            >
              Add User
            </Button>
          </HasAccess>
        </Panel.Header>
        <Panel.Content>
          <div>
            {error ? (
              <Error message={error} />
            ) : (
              <Table
                id="merchantUsers"
                data={memoUsers}
                columns={getUserDetailsTableColumns()}
                isLoading={isLoading}
                showPaginator={false}
                noRowsText="There are no merchant users to display."
                meta={{
                  accountHolderCode,
                  role: currentUserAccountData?.role,
                  onStatusSwitchToggle: (id: string, status: boolean) => {
                    setShowUpdateUserStatusModal(true);
                    setCurrentUserId(id);
                    setCurrentUserStatus(status);
                  },
                }}
              />
            )}
          </div>
        </Panel.Content>
      </Panel>
      <Modal
        isOpen={showSignUpModal}
        title="Sign Up"
        onClose={() => setShowSignupModal(false)}
      >
        <SignUpForm
          accountHolderInfo={{
            edition: accountConfigData?.edition ?? "",
            jackrabbitId: accountConfigData?.jrId ?? "",
            companyName: accountConfigData?.legalBusinessName ?? "",
          }}
          invitedUserEmail=""
          createUserStatus={status}
          onSubmit={handleCreateUserFormSubmit}
          onCancel={() => setShowSignupModal(false)}
        />
      </Modal>
      <HasAccess allowedRoles={["ADMIN"]}>
        <Modal
          isOpen={showUpdateUserStatusModal}
          title={"Update User Status"}
          titleClasses="text-left"
          panelClasses="min-w-[300px]"
          onClose={() => setShowUpdateUserStatusModal(false)}
        >
          <div className="mt-5 flex flex-col">
            <p className="py-2">
              Change status to {currentUserStatus ? "Inactive" : "Active"}?
            </p>
            <div className="mt-4 flex justify-center gap-x-16">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowUpdateUserStatusModal(false)}
                className="w-20"
              >
                No
              </Button>
              <Button
                size="sm"
                onClick={handleUpdateUserStatus}
                className="w-20 justify-center"
                disabled={isUpdatingUserStatus}
                icon={
                  isUpdatingUserStatus && (
                    <Spinner className="mt-0.5 mr-1 text-white" />
                  )
                }
              >
                Yes
              </Button>
            </div>
          </div>
        </Modal>
      </HasAccess>
    </>
  );
}

export default MerchantUsers;
